// components/LoginForm.tsx
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_ENDPOINT } from '../../configurations/global.config';
import { AuthContext } from '../../contexts/user-context/user-context';
import { UserType } from '../../enums/users';
import { LoginFormData, User } from '../../interfaces/common/authentication';
import { VerifyPhoneDialog } from './verify-phone-dialog';
import { format_phone_number } from '../../utils/phone_numbers';
import { MuiTelInput } from 'mui-tel-input';
import { isMobile, isTablet } from 'react-device-detect';
import { APP_VERSION_HEADER } from '../../utils/app_authentication';
import { useSnackBar } from '../../contexts/snackbar';
import axios from 'axios';
import LoginWithOTP from './login-with-otp';
import { motion } from 'framer-motion';
import { EmojiEmotions, Password } from '@mui/icons-material';
import confetti from 'canvas-confetti';
import { OrgSettingsContext } from '../../contexts/org-settings-context';
import { grey } from '@mui/material/colors';
import { formatAccountId } from './utils';

import { VisibilityOff, Visibility } from '@mui/icons-material';
import PasswordField from '../common/password-field/password-field';
import { Organisation } from '../../interfaces/delivery-company/orders';
import { DomainForcedOrganisationContext } from '../../contexts/domain-forced-organisation-context';

const defaultFormData: LoginFormData = {
  password: '',
  phone: '',
  userType: UserType.MERCHANT,
  organisation_id: ''
};

function launchConfetti() {
  confetti({
    particleCount: 80,
    spread: 90,
    origin: { y: 0.6 },
  });
}

function LoginForm() {
  const [formData, setFormData] = useState(defaultFormData);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [firstLogin, setFirstLogin] = useState(false);
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);

  const { setUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {color} = useContext(OrgSettingsContext)

  const [searchParams] = useSearchParams();

  const isInvited = searchParams.get('invited') === 'true';
  const invitedName = searchParams.get('name') || '';
  const org = searchParams.get('org') || ''
  const inviteePhone = searchParams.get('phone') || ''
  const redirectTo = searchParams.get('redirect_to') || '';

  const {forcedOrganisation} = useContext(DomainForcedOrganisationContext)

  const account_id = formatAccountId(formData.phone, formData.userType, formData.organisation_id);

  useEffect(() => {
    if (isInvited) {
      launchConfetti();
      setFormData({
          password: '',
          phone: inviteePhone,
          userType: UserType.MERCHANT,
          organisation_id: org,
      })
    }
  }, [isInvited]);

  useEffect(()=>{
    if (forcedOrganisation)
      setFormData({...formData, organisation_id: forcedOrganisation?.organisation_id})
  }, [forcedOrganisation])


  const handleLoginResponse = (data: any) => {
    const user: User = {
      id: data.user.id,
      name: data.user.name,
      phone_number: data.user.phone_number,
      type: data.user.type,
      authToken: data.access,
      authTokenRefresh: data.refresh,
      is_admin: data.user.is_admin || data.user.type === UserType.ORGANISATION,
      product_price_enabled: data.user.product_price_enabled,
    };

    if (user.type === UserType.ORGANISATION) {
      user.organisation = { pk: data.user.id, org_id: data.user.organisation_id };
    }
    if (user.type === UserType.EMPLOYEE) {
      user.branches = data.user.branches;
      user.organisation = {
        pk: data.user.organisation.id,
        org_id: data.user.organisation.organisation_id,
      };
      user.roles = data.user.roles;
    }
    if (user.type === UserType.MERCHANT || user.type === UserType.DRIVER) {
      user.organisation = {
        pk: data.user.organisation.id,
        org_id: data.user.organisation.organisation_id,
      };
      user.default_delivery_fee = data.user.default_delivery_fee;
      user.address = data.user.address;
      user.storage_key = data.user.storage_key;
    }

    setUser(user);
  };


  useEffect(() => {
    if (isInvited) {
      launchConfetti();
      setFormData({
        password: '',
        phone: inviteePhone,
        userType: UserType.MERCHANT,
        organisation_id: org,
      });
    }
  }, [isInvited]);


  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    if (!account_id || !formData.password) {
      setErrorMessage('Please enter phone number and password.');
      setIsLoading(false);
      return;
    }

    const payload = {
      account_id,
      password: formData.password,
    };

    try {
      const res = await axios.post(`${API_ENDPOINT}/v1/auth/token/`, payload, {
        headers: APP_VERSION_HEADER,
      });
      console.log(res)
      handleLoginResponse(res.data);
    } catch (err: any) {
      console.log(err)
      const res = err.response;
      if (!res || !res.data) {
        setErrorMessage('Unexpected error. Please contact support.');
      } else {
        const { code, message } = res.data;
        if (code === 'first_login_otp') {
          setRequiresOTP(true);
          setFirstLogin(true);
        } else if (code === 'otp_required') {
          setRequiresOTP(true);
        } else if (code === 'unverified_account') {
          setVerificationDialogOpen(true);
          setErrorMessage('Your account is not verified yet.');
        } else if (code === 'unactivated_account') {
          setErrorMessage(
            'Your account is not activated. Please contact your admin.'
          );
        } else if (code === 'account_suspended') {
          setErrorMessage('Your account has been suspended. Contact support.');
        } else {
          setErrorMessage('Invalid phone number or password.');
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (requiresOTP) {
    return (
      <LoginWithOTP
        account_id={account_id}
        password={formData.password}
        phone_number={formData.phone}
        firstLogin={firstLogin}
        onBack={() => setRequiresOTP(false)}
        onSuccess={handleLoginResponse}
        invitedName={invitedName}
      />
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        maxWidth: 480,
        margin: 'auto',
        padding: 4,
        borderRadius: 3,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        backgroundColor: '#ffffff',
      }}
    >
      {!isInvited && (
        <>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Welcome Back
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please enter your credentials to login
          </Typography>
        </>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
          {errorMessage}
        </Alert>
      )}

      {isInvited && (

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
            }}
          >
            <motion.div
              initial={{ rotate: -10 }}
              animate={{ rotate: [0, 10, -10, 10, 0] }}
              transition={{
                duration: 1.8,
                ease: 'easeInOut',
                repeat: Infinity,
              }}
            >
              <EmojiEmotions sx={{ fontSize: 50, color: color }} />
            </motion.div>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#333' }}>
                🎉 Welcome, {invitedName}!
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: '#555' }}>
                We are thrilled to have you join us!
              </Typography>
            </Box>
          </Box>
        </motion.div>
      )}

      <form onSubmit={handleSubmit}>
        <input type="hidden" value="prayer" />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Profile</InputLabel>
          <Select
            value={formData.userType}
            onChange={(e) =>
              setFormData({ ...formData, userType: e.target.value as UserType })
            }
            required
            label="Profile"
            variant="outlined"
          >
            {Object.values(UserType).map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {['employee', 'merchant', 'driver'].includes(formData.userType) && !forcedOrganisation && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <TextField
                autoComplete="new-password"
                id="outlined-organisation-input"
                label="Delivery Organisation Id"
                type="text"
                value={formData.organisation_id}
                disabled={isLoading}
                required
                onChange={(e) =>
                  setFormData({ ...formData, organisation_id: e.target.value  })
                }
              />
          </FormControl>
        )}

        <FormControl fullWidth sx={{ mt: 2 }}>
          <MuiTelInput
            label="Phone Number"
            defaultCountry="LB"
            forceCallingCode
            value={formData.phone}
            onChange={(phone) => setFormData({ ...formData, phone })}
            required
          />
        </FormControl>
        <Box sx={{ mt: 2 }}>
          <PasswordField
            label="Password "
            placeholder={isInvited ? 'Temporary Password' : 'Password'}
            value={formData.password}
            onPasswordChange={(value) =>
              setFormData({ ...formData, password: value })
            }
            variant="outlined"
          />
          {isInvited && (
            <Typography variant="body2" color={grey[700]}>
              <b>Use the temporary password shared over whatsapp.</b>
            </Typography>
          )}
        </Box>

        {isLoading && <LinearProgress sx={{ mt: 2 }} />}

        <Box display="flex" gap={2} mt={4}>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ borderRadius: 2, fontWeight: 600 }}
            disabled={isLoading}
          >
            Login
          </Button>

          <Button
            fullWidth
            variant="outlined"
            onClick={() => navigate(`/reset-password${redirectTo ? `?redirect_to=${encodeURIComponent(redirectTo)}` : ''}`)}
            sx={{ borderRadius: 2 }}
            disabled={isLoading}
          >
            Forgot Password
          </Button>
        </Box>
      </form>

      <VerifyPhoneDialog
        isOpen={verificationDialogOpen}
        phoneNumber={formData.phone}
        onSuccess={() => setVerificationDialogOpen(false)}
        closeVerificationDialog={() => setVerificationDialogOpen(false)}
        account_id={formatAccountId(
          formData.phone,
          formData.userType,
          formData.organisation_id
        )}
        redirectTo={redirectTo}
      />
    </Paper>
  );
}

export default LoginForm;
